'use client';

import React from 'react';
import styled from 'styled-components';
import { Dropdown } from '@/components/forms/controls/Dropdown/Dropdown';
import { Button } from '../Button/Button';
import { CounterDisplay } from '@/components/forms/controls/CounterDisplay/CounterDisplay';
import { Accordion } from '../Accordion/Accordion';

/**
 * ControlsPanel Component Props
 */
export interface ControlsPanelProps {
  /** Log source options */
  logSourceOptions: { label: string; value: string }[];
  /** Selected log source */
  selectedLogSource: string;
  /** Callback when log source changes */
  onLogSourceChange: (value: string) => void;
  /** Callback when refresh button is clicked */
  onRefresh: () => void;
  
  /** Active alert patterns count */
  activePatternCount: number;
  /** Callback when quick test button is clicked */
  onQuickTest?: () => void;
  
  /** Optional CSS class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Panel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 24px;
    gap: 16px;
  }
`;

const Section = styled.div`
  padding: 16px;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    
    button {
      width: 100%;
    }
  }
`;

// ============================================================================
// Component
// ============================================================================

/**
 * ControlsPanel Component
 * 
 * Controls panel for log browser with log source selection and advanced options.
 * All sections are displayed in a horizontal layout with two columns.
 * 
 * @component
 * @example
 * ```tsx
 * <ControlsPanel
 *   logSourceOptions={[{ label: 'full_data_logs.jsonl', value: 'full_data_logs' }]}
 *   selectedLogSource="full_data_logs"
 *   onLogSourceChange={setLogSource}
 *   onRefresh={handleRefresh}
 *   activePatternCount={11}
 *   onQuickTest={handleQuickTest}
 * />
 * ```
 */
export const ControlsPanel: React.FC<ControlsPanelProps> = ({
  logSourceOptions,
  selectedLogSource,
  onLogSourceChange,
  onRefresh,
  activePatternCount,
  onQuickTest,
  className,
}) => {
  return (
    <Panel className={className}>
      {/* Log Source Section */}
      <Accordion title="Log Source" defaultCollapsed={false}>
        <Section>
          <FieldGroup>
            <Dropdown
              label="Log Source"
              options={logSourceOptions}
              value={selectedLogSource}
              onChange={onLogSourceChange}
              variant="form"
            />
            <Button
              variant="secondary"
              onClick={onRefresh}
              fullWidth
            >
              Refresh Logs
            </Button>
          </FieldGroup>
        </Section>
      </Accordion>

      {/* Advanced Options Section */}
      <Accordion title="Advanced Options" defaultCollapsed={false}>
        <Section>
          <FieldGroup>
            <CounterDisplay
              count={activePatternCount}
              label="Active Alert Patterns"
              onClick={onQuickTest}
              animated
            />
            {onQuickTest && (
              <ButtonGroup>
                <Button
                  variant="secondary"
                  onClick={onQuickTest}
                  fullWidth
                >
                  Quick Test All Patterns
                </Button>
              </ButtonGroup>
            )}
          </FieldGroup>
        </Section>
      </Accordion>
    </Panel>
  );
};

export default ControlsPanel;

